import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { datasetApi } from '../api/search'
import { bookmarkApi, type BookmarkFolder } from '../api/bookmarks'

function getSessionIdFromStorage(): string | null {
  try {
    const saved = localStorage.getItem('pubmed_search_state')
    if (saved) {
      const state = JSON.parse(saved)
      return state.sessionId || null
    }
  } catch {
    // ignore
  }
  return null
}

export type AnalysisSource =
  | { type: 'search'; searchId: string | null; filteredPmids?: string[] }
  | { type: 'dataset'; datasetId: string; datasetName?: string }
  | { type: 'bookmark_folder'; folderId: string | 'unsorted'; folderName: string }

interface Props {
  /** Default source for the picker. Usually 'search' from the current page. */
  value: AnalysisSource
  onChange: (s: AnalysisSource) => void
  /** Current search context (used for the 'search' option label and filtered-pmids hint). */
  searchId?: string | null
  /** PMIDs the user is currently viewing on the search page (after filters). Used as the
   *  'filteredPmids' payload when source is 'search'. */
  currentFilteredPmids?: string[]
  /** Total count of articles in the current search (for the label). */
  currentTotalCount?: number
  /** Restrict which source types the user may pick. Defaults to all three. */
  allowedSources?: Array<AnalysisSource['type']>
  /** Section heading. Defaults to "Analyze articles from…". */
  heading?: string
  /** Notice shown when 'search' is disallowed. Pass empty string to hide. */
  restrictedNotice?: string
}

export default function AnalysisSourcePicker({
  value, onChange, searchId, currentFilteredPmids, currentTotalCount, allowedSources,
  heading = 'Analyze articles from…',
  restrictedNotice = 'This analysis can only run on a saved dataset or bookmark folder — the source must be reproducible.',
}: Props) {
  const allow = (t: AnalysisSource['type']) => !allowedSources || allowedSources.includes(t)
  const sessionId = getSessionIdFromStorage()

  const { data: datasets } = useQuery({
    queryKey: ['my-datasets-for-source-picker', sessionId],
    queryFn: () => datasetApi.listDatasets(sessionId || undefined),
  })

  const { data: folders } = useQuery({
    queryKey: ['bookmark-folders-for-source-picker'],
    queryFn: () => bookmarkApi.listFolders(),
  })

  const [datasetId, setDatasetId] = useState<string>('')
  const [folderId, setFolderId] = useState<string>('')

  // Keep local pickers in sync if parent sets a non-search source.
  useEffect(() => {
    if (value.type === 'dataset') setDatasetId(value.datasetId)
    if (value.type === 'bookmark_folder') setFolderId(String(value.folderId))
  }, [value.type, (value as any).datasetId, (value as any).folderId])

  // If the current selection is disallowed by allowedSources, switch to a valid one.
  useEffect(() => {
    if (!allowedSources || allowedSources.includes(value.type)) return
    if (allowedSources.includes('dataset') && datasets && datasets.length > 0) {
      const id = datasetId || datasets[0].dataset_id
      const ds = datasets.find((d: any) => d.dataset_id === id)
      onChange({ type: 'dataset', datasetId: id, datasetName: ds?.name })
    } else if (allowedSources.includes('bookmark_folder')) {
      const opts = bookmarkFolderOptions(folders)
      if (opts.length > 0) {
        const id = folderId || opts[0].id
        onChange({ type: 'bookmark_folder', folderId: id as any, folderName: opts.find(o => o.id === id)?.name || '' })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedSources, value.type, datasets?.length, folders?.length])

  const searchLabel = searchId
    ? `Current search results${currentFilteredPmids != null ? ` (${currentFilteredPmids.length.toLocaleString()})` : currentTotalCount ? ` (${currentTotalCount.toLocaleString()})` : ''}`
    : 'Current search results (no search loaded)'

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 mb-4 text-sm">
      <div className="text-xs font-semibold text-slate-600 mb-2">{heading}</div>
      {allowedSources && !allowedSources.includes('search') && restrictedNotice && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2">
          {restrictedNotice}
        </div>
      )}
      <div className="space-y-2">
        {allow('search') && (
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            checked={value.type === 'search'}
            onChange={() => onChange({ type: 'search', searchId: searchId || null, filteredPmids: currentFilteredPmids })}
            disabled={!searchId}
            className="accent-blue-600"
          />
          <span className={!searchId ? 'text-slate-400' : ''}>{searchLabel}</span>
        </label>
        )}

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            checked={value.type === 'dataset'}
            onChange={() => {
              if (datasets && datasets.length > 0) {
                const first = datasetId || datasets[0].dataset_id
                setDatasetId(first)
                const ds = datasets.find((d: any) => d.dataset_id === first)
                onChange({ type: 'dataset', datasetId: first, datasetName: ds?.name })
              }
            }}
            disabled={!datasets || datasets.length === 0}
            className="accent-blue-600"
          />
          <span className={!datasets || datasets.length === 0 ? 'text-slate-400' : ''}>
            Saved dataset
          </span>
          {value.type === 'dataset' && datasets && datasets.length > 0 && (
            <select
              value={datasetId}
              onChange={(e) => {
                const id = e.target.value
                setDatasetId(id)
                const ds = datasets.find((d: any) => d.dataset_id === id)
                onChange({ type: 'dataset', datasetId: id, datasetName: ds?.name })
              }}
              className="ml-2 px-2 py-1 border border-slate-300 rounded text-xs bg-white"
            >
              {datasets.map((d: any) => (
                <option key={d.dataset_id} value={d.dataset_id}>{d.name}</option>
              ))}
            </select>
          )}
        </label>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            checked={value.type === 'bookmark_folder'}
            onChange={() => {
              const opts = bookmarkFolderOptions(folders)
              if (opts.length > 0) {
                const first = folderId || opts[0].id
                setFolderId(first)
                onChange({ type: 'bookmark_folder', folderId: first as any, folderName: opts.find(o => o.id === first)?.name || '' })
              }
            }}
            className="accent-blue-600"
          />
          <span>Bookmark folder</span>
          {value.type === 'bookmark_folder' && (
            <select
              value={folderId}
              onChange={(e) => {
                const id = e.target.value
                setFolderId(id)
                const opts = bookmarkFolderOptions(folders)
                const opt = opts.find(o => o.id === id)
                onChange({ type: 'bookmark_folder', folderId: id as any, folderName: opt?.name || '' })
              }}
              className="ml-2 px-2 py-1 border border-slate-300 rounded text-xs bg-white"
            >
              {bookmarkFolderOptions(folders).map(o => (
                <option key={o.id} value={o.id}>{o.name}{o.count != null ? ` (${o.count})` : ''}</option>
              ))}
            </select>
          )}
        </label>
      </div>
    </div>
  )
}

function bookmarkFolderOptions(folders: BookmarkFolder[] | undefined): { id: string; name: string; count?: number }[] {
  const opts: { id: string; name: string; count?: number }[] = [{ id: 'unsorted', name: 'Unsorted' }]
  for (const f of folders || []) {
    opts.push({ id: f.folder_id, name: f.name, count: f.bookmark_count })
  }
  return opts
}
